import * as Sentry from "@sentry/react";

/**
 * Cookie / tracking consent (GDPR + ePrivacy).
 *
 * Essential cookies (auth tokens, sidebar state) load unconditionally — they
 * are necessary for the panel to work. Everything that records or profiles the
 * user is "non-essential" and only loads after explicit consent:
 *   - Sentry Session Replay (records the session)
 *   - LeadConnector chat widget (third-party)
 */

const CONSENT_KEY = "cookie-consent";

export type ConsentValue = "accepted" | "rejected";

export function getConsent(): ConsentValue | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === "accepted" || value === "rejected" ? value : null;
  } catch {
    return null;
  }
}

export function setConsent(value: ConsentValue) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // ignore (private mode / storage disabled)
  }
  if (value === "accepted") {
    loadNonEssential();
  }
}

let nonEssentialLoaded = false;

/** Load non-essential trackers. Safe to call multiple times. */
export function loadNonEssential() {
  if (nonEssentialLoaded) return;
  nonEssentialLoaded = true;

  // Sentry session replay — records the user session, needs consent.
  try {
    Sentry.addIntegration(Sentry.replayIntegration());
  } catch {
    // Sentry not initialized (e.g. dev) — ignore.
  }

  // LeadConnector chat widget (third-party).
  if (!document.getElementById("leadconnector-widget")) {
    const script = document.createElement("script");
    script.id = "leadconnector-widget";
    script.src = "https://widgets.leadconnectorhq.com/loader.js";
    script.setAttribute(
      "data-resources-url",
      "https://widgets.leadconnectorhq.com/chat-widget/loader.js",
    );
    script.setAttribute("data-widget-id", "6a0efaf2f4e3450dc8de2708");
    document.body.appendChild(script);
  }
}
